// Lightweight header for chart widgets. Lives in its own file so that
// CoinTerminalPage can import it eagerly while the actual chart body
// (ChartWidgetInstance + lightweight-charts) stays lazy-loaded.
//
// Shows per-instance whale/liq threshold dropdowns plus the coin picker.
// Only renders the dropdowns relevant to the pinned overlay set:
//   - chart-whales:        whale only
//   - chart-liquidations:  liq only
//   - chart-orders:        neither (orders overlay has its own size filter)
//   - chart (generic):     both

import React from 'react';
import { getCoinTier, DEFAULT_THRESHOLDS } from '../../../hooks/useThresholds';
import { FilterDropdown } from '../../CoinTerminalSections/sectionHelpers';
import { useWidgetSetting } from '../cockpitContext.jsx';
import CoinPickerHeader from './CoinPickerHeader';

// Threshold step set for chart overlay filtering. "All" is intentionally
// absent — the /large-trades fetch needs a positive minSize to return a
// render-tractable result; 500K is the practical "show as much as possible"
// choice.
const CHART_THRESHOLD_OPTIONS = Object.freeze([
  { label: '500K', value: 500_000 },
  { label: '1M', value: 1_000_000 },
  { label: '5M', value: 5_000_000 },
  { label: '10M', value: 10_000_000 },
  { label: '25M', value: 25_000_000 },
  { label: '50M', value: 50_000_000 },
]);

function tierDefaults(coin) {
  const tier = getCoinTier(coin);
  return tier === 'high'
    ? { whale: DEFAULT_THRESHOLDS.whaleHigh, liq: DEFAULT_THRESHOLDS.liqHigh }
    : { whale: DEFAULT_THRESHOLDS.whaleAlt, liq: DEFAULT_THRESHOLDS.liqAlt };
}

function ChartHeader({ instanceId, defaultCoin, forcedIndicators }) {
  const [coin] = useWidgetSetting(instanceId, 'coin', defaultCoin);
  const defaults = tierDefaults(coin);
  const [whale, setWhale] = useWidgetSetting(instanceId, 'whaleThreshold', defaults.whale);
  const [liq, setLiq] = useWidgetSetting(instanceId, 'liqThreshold', defaults.liq);

  const showWhale = !forcedIndicators || forcedIndicators.whaleBubbles || forcedIndicators.whaleMarkers;
  const showLiq = !forcedIndicators || forcedIndicators.liquidations;

  return (
    <>
      {showWhale && (
        <FilterDropdown
          icon="whale"
          ariaLabel="Whale chart threshold"
          tooltip="Минимальный объём китовой сделки"
          options={CHART_THRESHOLD_OPTIONS}
          value={whale}
          onChange={setWhale}
        />
      )}
      {showLiq && (
        <FilterDropdown
          icon="liq"
          ariaLabel="Liquidation chart threshold"
          tooltip="Минимальный объём ликвидации"
          options={CHART_THRESHOLD_OPTIONS}
          value={liq}
          onChange={setLiq}
        />
      )}
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
}

export default React.memo(ChartHeader);
